/*
 * The StepServices component is the step in reseller SO flow enabling service selection.
 * 
 * This takes the following props:
 *   auth                     - authentication object
 *   customer                 - customer for which this SO is being created
 *   project                  - project for which this SO is being created
 *   forceParentQuery         - function to force parent component to refetch data.  TODO: refactor
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';

import PropTypes from 'prop-types';
import AbEnvironment from '../auth/AbEnvironment';

import { styled } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, 
         Paper, Tooltip, Typography,
         TableContainer, Table, TableBody, TableCell,
         TableHead, TableRow, TableSortLabel } from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import { Add, HelpOutline } from '@mui/icons-material';

import AppBindStyles from '../../AppBindStyles';

import ConfirmationDialog from '../ui/ConfirmationDialog';
import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
import ServerError from '../ui/ServerError';
import ServiceTableRow from './ServiceTableRow';
import ServiceAutosuggest from '../app/ServiceAutosuggest';

import DeletePendingServiceMutation from './DeletePendingServiceMutation';
import RequestedBudgetAccountUpdateMutation from '../paymentsources/RequestedBudgetAccountUpdateMutation'
import RequestedBudgetProjectUpdateMutation from '../paymentsources/RequestedBudgetProjectUpdateMutation'

import AccountFetch from '../account/AccountFetch';

const abEnvironment = new AbEnvironment();
const StepServicesQuery = graphql`
query StepServicesQuery($accountId: UUID!, $projectId: UUID) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    accountNum
    accountName
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    initialBalanceAmount
    autoTopupAmount
    autoTopupThreshhold
    maxMonthlyBudget
    rAutoTopupAmount
    rAutoTopupThreshold
    rMaxMonthlyBudget
    preApproveSubs
    rPreApproveSubs
    projectTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        projectId
        projectName
        preApproveSubs
        rPreApproveSubs
        budgetApprovedCount
        walletTByCustomerWalletId {
          walletId
          autoTopupAmount
          autoTopupThreshold
          maxMonthlyBudget
          initialBalanceAmount
          rAutoTopupAmount
          rAutoTopupThreshold
          rMaxMonthlyBudget
        }
        walletTByResellerWalletId {
          walletId
          autoTopupAmount
          autoTopupThreshold
          maxMonthlyBudget
          initialBalanceAmount
          rAutoTopupAmount
          rAutoTopupThreshold
          rMaxMonthlyBudget
        }
      }
    }
    customerAppTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        paymentOrder
        appMarkupPercentage
        rPaymentOrder
        rPassThroughFees
        rAppMarkupPercentage
        pwd
        bindCardTByBcId {
          bcStatus
        }
      }
    }
    customerServiceTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        customerServiceId
        appId
        customerAccountId
        serviceStatus
        appSkuPrice
        paymentOrder
        passThroughFees
        billingFrequency
        billingDayOfMonth
        soAccepted
        accountTByCustomerAccountId {
          accountName
          contactFirstName
          contactLastName
          contactEmail
          accountType
          accountTByParentId {
            accountName
            contactFirstName
            contactLastName
            contactEmail
          }
        }
        appTByAppId {
          appName
          appSku
          appLogo
          appType
          description
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  button: {
    width: 52,
  },
  container: {
    position: 'relative',
  },
  divider: {
    height: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    textAlign: 'left',
    // maxWidth: 980
  },
  appTile: {
    padding: theme.spacing(2)
  },
  configGrid: {
    marginTop: 16,
  },
  sectionTitleFirst: {
    marginTop: 16,
    marginBottom: -8,
  },
  sectionTitle: {
    marginTop: 8,
  },
  customText: {
    margin: 8
  },
  textFieldNotes: {
    margin: 'theme.spacing(2)',
  },
  frequency: {
    margin: theme.spacing(1),
    width: 120,
    marginTop: -4,
    marginLeft: 8,
    textAlign: "right"
  },
  dayOfMonth: {
    margin: theme.spacing(1),
    width: 185,
    marginTop: -4,
    marginLeft: 8,
    textAlign: "right"
  },
  currencyTextField: {
    margin: theme.spacing(1),
    marginTop: -4,
    marginLeft: 8,
    width: 120,
    minWidth: 120,
    textAlign: 'right',
    paddingLeft: 0
  },
  billingBox: {
    marginTop: 16,
    marginBottom: 16
  },
  terms: {
    maxWidth: 210,
    minWidth: 210
  }
});

const StyledTable = styled(Table) (({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  //borderColor: theme.palette.colors.secondary[200],
  borderColor: theme.palette.divider,
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
    // backgroundColor: theme.palette.colors.secondary[200],
    backgroundColor: theme.palette.divider,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  /*
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  */
  height: 50, // Make the row heights consistent.  Smaller than this could be better, but then padding needs to be adjusted.
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

class StepServices extends React.Component {
  
  state = {
    sortBy: 'status',
    sortOrder: 'asc',

    showAllServices: false,
    showSpinner: false,

    nextDisabled: false,

    accepted:    false,
    deleteConfirm: false, 
    serviceToDelete: null,
    fetchCount: 0,
    bcUser: {
      userId:         null,
      usingAccountId: null,
      accountName:    null,
      description:    null,
      lastName:       null,
      firstName:      null,
      email:          null,
      phone:          null,
      address1:       null,
      address2:       null,
      city:           null,
      state:          null,
      country:        null,
      postalCode:     null,
    },
    pbcUser: {
      userId:         null,
      usingAccountId: null,
      accountName:    null,
      description:    null,
      lastName:       null,
      firstName:      null,
      email:          null,
      phone:          null,
      address1:       null,
      address2:       null,
      city:           null,
      state:          null,
      country:        null,
      postalCode:     null,
    }
  }

  componentDidMount() {

    //
    // Hydrate the bcUser here.
    // - if an account_id has been passed, this will be the using account for the
    //   subscription.  If the using account is not the same as the current user
    //   account (provisioner), lookup address information from the db.  Otherwise,
    //   set from the account info in the context
    //

    // Start with the provisioning account
    let thePbcUser = {
      userId:         abEnvironment.getUserAttribute('u_id'),
      usingAccountId: abEnvironment.getUserAttribute('a_id'),
      accountName:    abEnvironment.getUserAttribute('a_accountName'),
      description:    abEnvironment.getUserAttribute('a_description'),
      lastName:       abEnvironment.getUserAttribute('a_lastName'),
      firstName:      abEnvironment.getUserAttribute('a_firstName'),
      email:          abEnvironment.getUserAttribute('a_email'),
      phone:          abEnvironment.getUserAttribute('a_phone'),
      address1:       abEnvironment.getUserAttribute('a_address1'),
      address2:       (abEnvironment.getUserAttribute('a_address2') ? abEnvironment.getUserAttribute('a_address2') : ''),
      city:           abEnvironment.getUserAttribute('a_city'),
      state:          abEnvironment.getUserAttribute('a_state'),
      country:        abEnvironment.getUserAttribute('a_country'),
      postalCode:     abEnvironment.getUserAttribute('a_postalCode')
    };
     
    let usingAccountId = this.props.customer.accountId;
    if (usingAccountId != null && usingAccountId !== thePbcUser.usingAccountId) {
      // Lookup the using account id
      AccountFetch.fetch(usingAccountId).toPromise()
      .then(af => {
        let theBcUser = {
          userId:         abEnvironment.getUserAttribute('u_id'), // Still use provisioner user id
          usingAccountId: usingAccountId,
          accountName:    af.accountTByAccountId.accountName,
          description:    af.accountTByAccountId.description,
          lastName:       af.accountTByAccountId.contactLastName,
          firstName:      af.accountTByAccountId.contactFirstName,
          email:          af.accountTByAccountId.contactEmail,
          phone:          af.accountTByAccountId.contactPhone,
          address1:       af.accountTByAccountId.address1,
          address2:       af.accountTByAccountId.address2,
          city:           af.accountTByAccountId.city,
          state:          af.accountTByAccountId.state,
          country:        af.accountTByAccountId.country,
          postalCode:     af.accountTByAccountId.postalCode
        };
        this.setState({bcUser: theBcUser, pbcUser: thePbcUser});
      })
      .catch(err => {
        this.setState({bcUser: thePbcUser, pbcUser: thePbcUser});
      });
    } else {
      this.setState({bcUser: thePbcUser, pbcUser: thePbcUser});
    }
  }

  isBackDisabled() {
    if (this.props.activeStep === 0) {
      return true;
    }

    return false;
  }

  /**
   * If the state is set to disable the button, then it is disabled.  That's an override.
   * 
   * Even if the state says it should be enabled, if there is nothing to do (no pending changes)
   * then keep it disabled.
   * 
   * @param {*} queryProps 
   * @returns 
   */
  isNextDisabled(pendingServicesCount, subscriptionChanges, budgetChanged) {
    console.log('StepServices.isNextDisabled: pendingServicesCount=' + pendingServicesCount + ', subscriptionChanges=' + subscriptionChanges + ', budgetChanged=' + budgetChanged)
    if (this.state.nextDisabled === true) {
      return true;
    }

    //
    // Check to make sure that something is changed that would allow for an SO to be sent.
    //
    let somethingChanged = false;
    if (pendingServicesCount > 0 || subscriptionChanges > 0 || budgetChanged === true) {
      somethingChanged = true;
    }
    if (somethingChanged === false) {
      return true;
    }

    if (this.props.activeStep === this.props.assignedStep) {
      return false;
    }
    return false;
  }

  /**
   * This is passed to the ServiceAutosuggest component.  When the autosuggest component is used to select
   * a service, it calls this function to disable the next button.  Next is re-enabled when cancelling
   * the service creation.  However, not that the state value doesn't fully govern the next button.  If the
   * state requires that the button be disabled (e.g. when the autosuggest has control), it will be disabled.
   * However, in some cases, the state can be false, and the button may still be disabled.
   */
  setDisableNext = (value) => {
    if (value === true || value === false) {
      this.setState({nextDisabled: value});
    }
  }

  getValueForRender(state_member, prop_member, theDefault)
  {
    if (state_member != null)
      return state_member;

    if (prop_member != null)
      return prop_member;

    return theDefault;
  }

  showSpinner() {
    return this.state.showSpinner;
  }

  confirmDelete = (customerServiceId) => {
    //a little weird reusing the payoutToApprove state but for convenience.
    this.setState({deleteConfirm: true, serviceToDelete: customerServiceId});
  }

  toggleOrder = (ob) => {
    if (ob === this.state.sortBy) {
      if (this.state.sortOrder === 'desc') {
        this.setState({sortOrder: 'asc'});
      } else {
        this.setState({sortOrder: 'desc'});
      }
    } else {
      this.setState({sortBy: ob, sortOrder: 'asc'});
    }
  } 

  render() {
    const { logError } = this.props.auth;
    const classes = this.props.classes;

    let displayOnly = false;
    if (this.props.activeStep > this.props.assignedStep) {
      displayOnly = true;
    }

    let skipAllowed = false;
   
    let usingAccountId = this.props.customer.accountId;
    let provisioningAccountId = abEnvironment.getUserAttribute('a_id');

    // Was a project passed in?  If so, the subscription is for a project.
    // If not, legacy case.
    let aProject = this.props.project;
    let projectId = null;
    if (aProject != null && aProject.projectId != null) {
      projectId = aProject.projectId;
    }

    let usingAccountName = ((this.state.bcUser.accountName) ? this.state.bcUser.accountName : 'Customer');

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={StepServicesQuery}
        variables={{accountId: usingAccountId, projectId: projectId, fetchCount: this.state.fetchCount}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            let theAccount = props.accountTByAccountId;

            // Project if relevant
            let theProject = null;
            if (theAccount.projectTsByCustomerAccountId.totalCount > 0) {
              theProject = theAccount.projectTsByCustomerAccountId.nodes[0];
            }

            // Setup an array of all active and pending services
            let allServices = [];
            for (let i = 0; i < theAccount.customerServiceTsByCustomerAccountId.nodes.length; i++) {
              let service = theAccount.customerServiceTsByCustomerAccountId.nodes[i];
              //exclude cancelled subs
              if (service.serviceStatus !== 2) {
                if (service.billingFrequency !== 0) {
                  allServices.push(service);
                } else if (service.billingFrequency === 0 && service.soAccepted === 0) {
                  allServices.push(service);
                }
              }
            }

            /*
             * In display only mode, we're going to show just pending services, so
             * put those in a separate array - pendingServices.
             * 
             * allServices will be sorted in edit mode, where the user can choose a number of sorting options.
             * pendingServices will sort alphabetically.
             * 
             * We'll also categorize services so that we can display a reasonable message.
             */

            let pendingServices = [];
            let releasedServices = [];
            let pendingServicesCount = 0;
            let releasedServicesCount = 0;

            for (let i = 0; i < allServices.length; i++) {
              let service = allServices[i];

              if (service.soAccepted === 0) {
                pendingServices.push(service);
              } else if (service.soAccepted === 1) {
                releasedServices.push(service);
              }
            }
            pendingServicesCount = pendingServices.length;
            releasedServicesCount = releasedServices.length;

            if (!displayOnly) {
              //
              // Sort subscriptions by the user's selected sort by / direction.
              //
              if (allServices.length > 1) {
                // Sort depends on sorting order in state
                if (this.state.sortBy === 'status') {
                  /* 
                  * Status means is it released or pending
                  */
                  if (this.state.sortOrder === 'desc') {
                    allServices.sort(function(a, b) {
                      /* Sort by status, then app name alpha */
                      if (a.soAccepted < b.soAccepted) { return 1; }
                      if (b.soAccepted < a.soAccepted) { return -1; }
                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                      return 0;
                    });
                  } else {
                    allServices.sort(function(a, b) {
                      /* Sort by status, then app name alpha */
                      if (a.soAccepted < b.soAccepted) { return -1; }
                      if (b.soAccepted < a.soAccepted) { return 1; }
                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                      return 0;
                    });
                  }                  
                } else {
                  // Sort by app name
                  if (this.state.sortOrder === 'desc') {
                    allServices.sort(function(a, b) {
                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return 1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return -1; }
                      return 0;
                    });
                  } else {
                    allServices.sort(function(a, b) {
                      if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                      if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                      return 0;
                    });
                  }
                }
              }

              //
              // Service block title
              //
              let serviceTitle = "Services for my customer";
              if (this.state.bcUser.accountName) {
                serviceTitle = "Services for " + this.state.bcUser.accountName;
              }

              let serviceTooltip = `
              In addition to purchasing subscriptions for your customer, you can 
              also bill your own services through AppBind. You can set up monthly 
              recurring services (e.g. a monthly managed service fee that you 
              charge your customer), and one-time services (e.g. a project setup fee).
              `;

              //
              // Services block detail
              //
              let serviceDetail = "You haven't added any services.";
              if (allServices.length > 0) {
                serviceDetail = "There ";
                if (releasedServicesCount > 0) {
                   if (releasedServicesCount === 1) {
                    serviceDetail = serviceDetail + "is 1 existing service";
                  } else {
                    serviceDetail = serviceDetail + "are " + releasedServicesCount + " existing services";
                  }
                  if (pendingServicesCount === 0) {
                    serviceDetail = serviceDetail + ".";
                  } else {
                    serviceDetail = serviceDetail + " and ";
                  }
                }
                if (pendingServicesCount === 1) {
                  if (releasedServicesCount > 0) {
                    serviceDetail = serviceDetail + "1 new service.";
                  } else {
                    serviceDetail = serviceDetail + "is 1 new service.";
                  }
                } else {
                  if (releasedServicesCount > 0) {
                    serviceDetail = serviceDetail + pendingServicesCount + " new services.";
                  } else {
                    serviceDetail = serviceDetail + "are " + pendingServicesCount + " new services.";
                  }
                }
              }

              //
              // Are there subscription changes or budget changes?  We'll look them
              // up here so that we can pass information to determine if the next
              // button should be active.
              //
              // TODO: restructure the SO such that all querying is at the SO
              // component, and data is passed down to steps.
              //
              let subscriptionChanges = 0;
              for (let i = 0; i < theAccount.customerAppTsByCustomerAccountId.nodes.length; i++) {
                let sub = theAccount.customerAppTsByCustomerAccountId.nodes[i];
                //exclude cancelled subs
                if (sub.appStatus === 2) {
                  continue;
                }

                if (sub.paymentOrder === 0 || sub.paymentOrder === 1 || sub.paymentOrder === 2) {
                  if (sub.bindCardTByBcId.bcStatus === -1) {
                    subscriptionChanges++; // new
                  // } else if (sub.rPassThroughFees !== null || sub.rAppMarkupPercentage !== null || sub.rPaymentOrder != null) {
                  } else if (sub.rPassThroughFees !== null || (sub.rAppMarkupPercentage !== null && sub.rAppMarkupPercentage !== sub.appMarkupPercentage) || (sub.rPaymentOrder != null && sub.rPaymentOrder !== sub.paymentOrder && ! (sub.rPaymentOrder === 0 && sub.paymentOrder === 1))) {
                    subscriptionChanges++; // changed
                  }
                } else if (sub.paymentOrder === 4 || sub.bindCardTByBcId.bcStatus === 4) {
                  if (sub.pwd == null) {
                    subscriptionChanges++; // new
                  }
                }
              }

              // approval
              if (theProject != null) {
                if (theProject.preApproveSubs === 0 && theProject.rPreApproveSubs === 1) {
                  subscriptionChanges++;
                }
              } else {
                if (theAccount.preApproveSubs === 0 && theAccount.rPreApproveSubs === 1) {
                  subscriptionChanges++;
                }
              }

              // budget changes
              let budgetChanged = false;
              let budgetExists = false;

              if (theProject != null) {
                let theCustomerWallet = theProject.walletTByCustomerWalletId;
                let theResellerWallet = theProject.walletTByResellerWalletId;
                if (theCustomerWallet != null && theCustomerWallet.autoTopupAmount > 0) {
                  budgetExists = true;
                } else if (theResellerWallet != null && theResellerWallet.autoTopupAmount > 0) {
                  budgetExists = true;
                }

                if (budgetExists === false) {
                  if (theCustomerWallet != null && (theCustomerWallet.rAutoTopupAmount > 0 || theCustomerWallet.initialBalanceAmount > 0 || theCustomerWallet.rMaxMonthlyBudget > 0) ) {
                    budgetChanged = true;
                  } else if (theResellerWallet != null && (theResellerWallet.rAutoTopupAmount > 0 || theResellerWallet.initialBalanceAmount > 0 || theResellerWallet.rMaxMonthlyBudget > 0) ) {
                    budgetChanged = true;
                  }
                } else {
                  if (theCustomerWallet != null && 
                      (theCustomerWallet.rAutoTopupAmount    !== theCustomerWallet.autoTopupAmount     ||
                       theCustomerWallet.rAutoTopupThreshold !== theCustomerWallet.autoTopupThreshold ||
                       theCustomerWallet.rMaxMonthlyBudget   !== theCustomerWallet.maxMonthlyBudget       ) ) {
                    budgetChanged = true;
                  } else if (theResellerWallet != null &&
                             (theResellerWallet.rAutoTopupAmount    !== theResellerWallet.autoTopupAmount     ||
                              theResellerWallet.rAutoTopupThreshold !== theResellerWallet.autoTopupThreshold ||
                              theResellerWallet.rMaxMonthlyBudget   !== theResellerWallet.maxMonthlyBudget       ) ) {
                    budgetChanged = true;
                  }
                }
              } else {
                if (theAccount.autoTopupAmount > 0) {
                  budgetExists = true;
                }

                if (budgetExists === false && (theAccount.rAutoTopupAmount > 0 || theAccount.initialBalanceAmount > 0 || theAccount.rMaxMonthlyBudget > 0)) {
                  budgetChanged = true;
                } else {
                  if (theAccount.rAutoTopupAmount    !== theAccount.autoTopupAmount     ||
                      theAccount.rAutoTopupThreshold !== theAccount.autoTopupThreshhold ||
                      theAccount.rMaxMonthlyBudget   !== theAccount.maxMonthlyBudget       ) {
                    budgetChanged = true;
                  }
                }
              }

              // So we've got subscriptionChanges and budgetChanged

              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper className={classes.paper}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Grid container spacing={0}>
                            <Grid item>
                              {/* Subscriptions I'll get for <customer> */}
                              <Typography align="left" variant="h6">{serviceTitle}</Typography>
                            </Grid>                  
                            <Grid item>
                              <Tooltip placement='bottom' title={serviceTooltip}>
                                <HelpOutline fontSize="small" style={{verticalAlign: "-32%", marginLeft: '4px'}}/>
                              </Tooltip>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography align="left" variant="body2">{serviceDetail}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={1}>
                            {allServices.length >= 0 &&
                            <Grid item xs={12}>
                              <TableContainer>
                                <StyledTable>
                                  <TableHead>
                                    <TableRow>
                                      <StyledTableCell sx={{ borderTopLeftRadius: 8}}>
                                        {allServices.length > 1 &&
                                        <TableSortLabel
                                          active={this.state.sortBy === 'status'}
                                          direction={this.state.sortOrder}
                                          onClick={() => this.toggleOrder('status') }>
                                          <Add/>
                                        </TableSortLabel>}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {allServices.length > 1 &&
                                        <TableSortLabel
                                          active={this.state.sortBy === 'appname'}
                                          direction={this.state.sortOrder}
                                          onClick={() => this.toggleOrder('appname') }>
                                          Service
                                        </TableSortLabel>}
                                        {allServices.length === 1 &&
                                        <React.Fragment>Service</React.Fragment>}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {allServices.length >= 1 &&
                                        <React.Fragment>Amount</React.Fragment>}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {allServices.length >= 1 &&
                                        <React.Fragment>Billing freqency</React.Fragment>}
                                      </StyledTableCell>
                                      <StyledTableCell>
                                        {allServices.length >= 1 &&
                                        <React.Fragment>Billing day</React.Fragment>}
                                      </StyledTableCell>
                                      <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {allServices.map(n => {
                                      return (
                                        <ServiceTableRow 
                                          key={n.customerAppId}
                                          service={n}
                                          styledTableRow={StyledTableRow}
                                          styledTableCell={StyledTableCell}
                                          editable={true}
                                          confirmDelete={this.confirmDelete}
                                        />
                                      );
                                    })}
                                    <ServiceAutosuggest
                                      auth={this.props.auth}
                                      addService={this.addService}
                                      usingAccountId={usingAccountId}
                                      usingAccountName={usingAccountName}
                                      provisioningAccountId={provisioningAccountId}
                                      setDisableNext={this.setDisableNext}
                                      placeholder="Add a service you provide to the project"
                                      suggestDefaults={false}
                                      autoFocus={true}
                                    />
                                  </TableBody>
                                </StyledTable>
                              </TableContainer>
                            </Grid>}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Dialogs */}
                  <ConfirmationDialog
                    open={this.state.deleteConfirm}
                    onClose={() => { this.setState({deleteConfirm:false, subscriptionToDelete: null});}}
                    onConfirm={() => {this.deleteService() }}
                    onCancel={() => { }}
                    title="Remove service?"
                    cancelBtn="Cancel"
                    okBtn="Ok"
                    irreversibleAction={true}
                  >
                    If you proceed, this service will be removed.
                  </ConfirmationDialog>  

                  {this.props.activeStep === this.props.assignedStep &&
                  <Grid item xs={12}>
                    <Button
                      disabled={this.isBackDisabled()}
                      color="primary" variant="outlined"
                      onClick={() => this.props.handleBack()}
                      style={AppBindStyles.bottomButton}>
                      Back
                    </Button>

                    {!this.showSpinner() &&        
                    <Button
                      color="primary" variant="contained"
                      disabled={this.isNextDisabled(pendingServicesCount, subscriptionChanges, budgetChanged)}
                      onClick={() => { this.handleNext(pendingServicesCount, theAccount);}}
                      style={AppBindStyles.bottomButton}>
                        Next
                    </Button>}
                    {this.showSpinner() &&
                    <Button color="primary" 
                            variant="contained"
                            style={AppBindStyles.bottomButton}>
                        <SavingSpinner/>
                    </Button>}

                    {skipAllowed &&
                      <Button
                      color="primary" variant="outlined"
                      onClick={() => { this.props.handleNext(pendingServicesCount, theAccount);}}
                      style={AppBindStyles.bottomButton}>
                      Skip
                    </Button>}
                  </Grid>} 
              </Grid>
              );
          
            } else {
              //display only

              //
              // Service block title
              //
              let serviceTitle = "Services changes";
              /*
              if (this.state.bcUser.accountName) {
                serviceTitle = "Services changes for " + this.state.bcUser.accountName;
              }
              */

              //
              // Services block detail
              //
              let serviceDetail = "You haven't added any services.";
              if (allServices.length > 0) {
                if (pendingServicesCount === 0) {
                  serviceDetail = "There aren't any new services.";
                } else if (pendingServicesCount === 1) {
                  serviceDetail = "There is 1 new service.";
                } else {
                  serviceDetail = "There are " + pendingServicesCount + " new services.";
                }
              }

              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={0}>
                      <Grid item>
                        <Typography align="left" variant="h6">{serviceTitle}</Typography>
                      </Grid>                  
                      <Grid item xs={12}>
                        <Typography align="left" variant="body2">{serviceDetail}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  { pendingServicesCount > 0 &&
                  <Grid item xs={12}>
                    <TableContainer>
                      <StyledTable>
                        <TableHead>
                          <TableRow>
                            <StyledTableCell sx={{ borderTopLeftRadius: 8}}/>
                            <StyledTableCell>Service</StyledTableCell>
                            <StyledTableCell>Amount</StyledTableCell>
                            <StyledTableCell>Billing freqency</StyledTableCell>
                            <StyledTableCell>Billing day</StyledTableCell>
                            <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingServices.map(n => {
                            return (
                              <ServiceTableRow 
                                key={n.customerAppId}
                                service={n}
                                styledTableRow={StyledTableRow}
                                styledTableCell={StyledTableCell}
                                editable={false}
                                showModel={true}
                              />
                            );
                          })}
                        </TableBody>
                      </StyledTable>
                    </TableContainer>
                  </Grid>}
                </Grid>
              );        
            }
          }
          if (!displayOnly) {
            return <div><LoadingSpinner/></div>
          }
        }
      }
      />
    );
  }

  handleNext(pendingServicesCount) {
    this.setState({showSpinner: false, showAllServices: false});    
    this.props.handleNext(pendingServicesCount);   
  }

  /**
   * Add service is passed to the ServiceAutosuggest component.  When a service is selected there,
   * that component calls this passed addService function to add the service to the set
   * of pending services.
   * 
   * @param {*} cApp 
   */
  addService = (cApp) => {
 
    const { callPartnerApi } = this.props.auth;
    let fetchCount = this.state.fetchCount;
    fetchCount++; 

    let aProject = this.props.project;
    let projectId = null;
    if (aProject != null && aProject.projectId != null) {
      projectId = aProject.projectId;
    }

    this.setState({ saveStatus: "saving"});

    let usingAccount = this.state.bcUser;
    let usingAccountId = usingAccount.usingAccountId;
    let usingAccountName = usingAccount.accountName;

    let provisioningAccountId = abEnvironment.getUserAttribute('a_id');
    let provisioningAccountName = abEnvironment.getUserAttribute('a_accountName');
  
    let returnedAppId = null;
    let returnCustomerServiceId = null;

    //for on the fly service additions, default description to name
    if (cApp.description === '') {
      cApp.description = cApp.appName
    }

    // Initiate promise chain.
    Promise.resolve()  
    .then(() => {
      // Call the server side provisioning
      let aPjwt = abEnvironment.getPjwt();

      let payload = {
        operation: "createServiceForProject",
        params: {
          pJwt:                aPjwt,
          accountId:           usingAccountId,
          accountName:         usingAccountName,
          pAccountId:          provisioningAccountId,
          pAccountName:        provisioningAccountName,
          projectId:           projectId,
          appId:               cApp.appId,
          appAccountId:        cApp.accountId,
          appStatus:           cApp.appStatus,
          appName:             cApp.appName,
          appSku:              cApp.appSku,
          appLogo:             cApp.appLogo,
          description:         cApp.description,
          notes:               cApp.notes,
          appType:             cApp.appType,
          parentAppId:         cApp.parentAppId,
          oemAppId:            cApp.oemAppId,
          atId:                cApp.atId,
          appSkuPrice:         cApp.appSkuPrice,
          paymentOrder:        cApp.paymentOrder,
          passFees:            cApp.passThroughFees,
          soEntered:           1,
          billingFrequency:    cApp.billingFrequency,
          billingDayOfMonth:   cApp.billingDayOfMonth
        }
      };

      return callPartnerApi('appbind/generic',
                            'POST',
                              payload);
    })
    .then((response) => {
      return response.json();
    })
    .then((jresp) => {

      // - the return from the new flow is on a data attribute
      //set appId if a new one was created
      returnedAppId = jresp.data.appId;
      returnCustomerServiceId = jresp.data.custServiceId;

      if (returnCustomerServiceId)
      {
        this.setState({
          appId: returnedAppId,
          createdCustomerAppId: returnCustomerServiceId,
          saveStatus: 'success',
          saveMessage: 'Service created!',
          fetchCount: fetchCount,
        });
      }
      else
      {
        this.setState({
          saveStatus: 'error',
          saveMessage: 'Unable to create the service: ' + JSON.stringify(jresp)
        });
      }
    }).then(() => {
      //every time a service is added, we set the latest_budget_approved off.  This looks a little funky but inside this budget update
      //it will always set budget approved off if called.
      if (returnCustomerServiceId)
      {
        if (projectId != null) {
          RequestedBudgetProjectUpdateMutation.commit(this.props.project.projectId, 0);
        } else {
          RequestedBudgetAccountUpdateMutation.commit(usingAccountId, null, null, null, null, null, null, null, null, null, null, null);
        }
      }
      this.props.forceParentQuery();
    }).catch((error) => {
      this.setState({
        saveStatus: 'error',
        saveMessage: 'There was a problem creating the subscription.  Please contact support.'
      });
    });
  };
  
  deleteService = () => {
    DeletePendingServiceMutation.commit(this.state.serviceToDelete)
    .then(response => {
      let fetchCount = this.state.fetchCount;
      fetchCount++;
      this.setState({ fetchCount: fetchCount });
      this.props.forceParentQuery();
    })
    .catch(error => {
      // console.log('Got error: ' + error);
    });
  }  
}


StepServices.propTypes = {
  assignedStep: PropTypes.number.isRequired,
  activeStep: PropTypes.number.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default withStyles(styleSheet)(StepServices);
